import { isValid as isValidDriverLicense } from 'usdl-regex';
import { validate } from 'vin-validator';
import creditCardType from 'credit-card-type';
import some from 'lodash/some';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import { startsWith, reduce, find } from 'lodash';

import { parseFullName } from '@jerry-serverless/name-parser';

import { CardTypeType } from './cc';
import { PARTIAL_VIN_SUFFIX, PARTIAL_VIN_YEAR_CUTOFF } from './vin';
import { states } from './states';

/**
 * Make sure there is a first and last name with at least 2 characters each.
 */
export const validFullname = (val: string | null): boolean => {
  if (!val) {
    return false;
  }
  const nameObj = parseFullName(val.trim(), 'all', -1, 0, 1);
  const first = (nameObj.first || '').replace(/[.|\d]/g, ' ').trim();
  const last = (nameObj.last || '').replace(/[.|\d]/g, ' ').trim();

  return first.length > 1 && last.length > 1;
};

/**
 * Check validity of name passed in through partner param.
 * This is to sanitize the case where partners were sending over {fname} or {lname} as a value to param.
 */
export const validPartnerNameParam = (fname: string | null, lname: string | null): boolean => {
  if (!fname || !lname) {
    return false;
  }
  const name = `${fname} ${lname}`;

  return !/\{.+\}/.test(name) && validFullname(name);
};

/**
 * Email validation
 */
export const validEmail = (val: string | null): boolean => {
  if (!val) {
    return false;
  }

  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(val);
};

/**
 * A numeric string of length X
 */
export const validNumeric = (val: string | null, len?: number, required = true): boolean => {
  if (!val) {
    return !required;
  }
  const exp = len ? `^[0-9]{${len}}$` : '^[0-9]+$';
  const re = new RegExp(exp);
  return re.test(val);
};

/**
 * Phone validation
 */
export const validPhone = (phone: string | null): boolean => validNumeric(phone, 10);

/**
 * VIN validation
 * Only validates the format of VIN passed in.
 */
export const VIN_LENGTH = 17;
export const validateVIN = (VIN: string): boolean => {
  const vin = VIN.toLocaleUpperCase();
  if (vin.length !== VIN_LENGTH) return false;

  // Check for partial vin format
  const serial = vin.substring(10, 17);
  return !(serial === '1111111' || serial === '*******');
};

/**
 * VIN validation - checks whether partial/full VIN format is correct.
 * This validation allows leading and trailing spaces.
 */
export const validatePartialVIN = (VIN: string, isPartial = false, year?: string): boolean => {
  const vin = VIN.trim().toLocaleUpperCase();

  const isEndsWithSubfix = vin.slice(10) === PARTIAL_VIN_SUFFIX;

  // Validate Full VINs
  if (!(isPartial || isEndsWithSubfix) && !validate(vin)) {
    return false;
  }

  // If year was provided as an argument, check that it parses correctly
  if (year && isNaN(parseInt(year, 10))) {
    return false;
  }

  // Validate Partial VINs
  if (year && parseInt(year, 10) >= PARTIAL_VIN_YEAR_CUTOFF && isEndsWithSubfix && vin.length !== 17) {
    return false;
  }

  return true;
};

/**
 * Check if driver license is valid.
 *
 * @param number
 * @param state
 */
export const validateDL = (number: string, state: string): boolean => {
  try {
    return isValidDriverLicense(state, number);
  } catch (e) {
    return false;
  }
};

/**
 * Validate credit card number
 *
 * @param num
 * @param types
 */
export const validateCardNumber = (
  num: string,
  types: CardTypeType[] = [CardTypeType.Visa, CardTypeType.Mastercard]
): boolean => {
  const creditCardTypes = creditCardType(num);
  return num && num.length > 0
    ? some(
        creditCardTypes,
        (creditCard) => types.includes(creditCard.type as CardTypeType) && includes(creditCard.lengths, num.length)
      )
    : false;
};

/**
 * Check if value is an empty value
 */
export const isEmptyValue = (value: unknown): boolean => {
  if (typeof value === 'number') {
    return !value;
  }
  if (value && typeof value === 'object') {
    return Object.values(value).every(isEmptyValue);
  }
  return isEmpty(value);
};

/**
 * Check if the given value is a valid US zip code
 * @param {*} value The value to validate
 */
export const validZip = (value: string): boolean => validNumeric(value, 5);

/**
 * Converts a deep link string into an object representation.
 * If a title is present, links used in chat are returned in markdown format.
 *
 * @example
 * Input: "[Wallet](chat->getjerry://wallet), (sms->https://app.jerry.ai/81bk)"
 * Output: {
 *   chat: '[Wallet](getjerry://wallet)',
 *   sms: 'https://app.jerry.ai/81bk'
 * }
 *
 * @param linkString - e.g. "[Wallet](chat->getjerry://wallet), (sms->https://app.jerry.ai/81bk)"
 */
export const parseDeepLinkString = (linkString: string): Record<string, string> => {
  const titleRegex = /\[([^\]]+)\]/;
  const linkRegex = /\((\w+)->([^)]+)\)/g;

  // Capture the title
  const titleMatch = linkString.match(titleRegex);
  const title = titleMatch ? titleMatch[1] : undefined;

  if (!title) {
    throw new Error('Deep link string should contain a title');
  }

  // Capture the links
  const matchesArray = Array.from(linkString.matchAll(linkRegex));

  return reduce(
    matchesArray,
    (deeplinkPairs, match) => {
      if (!isValidDeepLink(match[2])) {
        throw new Error(`Invalid deep link detected: ${match[2]}`);
      }

      // If the link type is "chat", wrap it with markdown format, else just return the link
      const linkValue = match[1] === 'chat' ? `[${title}](${match[2]})` : match[2];

      return {
        ...deeplinkPairs,
        [match[1]]: linkValue,
      };
    },
    {}
  );
};

/**
 * Test if the URL is an internal link
 * A valid internal link should start with https://getjerry.com/app, https://app.jerry.ai or getjerry://
 *
 * @param url
 */
export const isValidDeepLink = (url: string): boolean => {
  return (
    startsWith(url, 'https://getjerry.com/app') ||
    startsWith(url, 'https://app.jerry.ai') ||
    startsWith(url, 'getjerry://')
  );
};

/**
 * Check if state is valid. Support lowercase and uppercase
 */
export const validateState = (value?: string): boolean => {
  if (!value) {
    return false;
  }
  return !!find(states, (state) => state.abbreviation === value.toUpperCase());
};
